import { useState, useEffect } from "react";

const Logo = ({ size = 28 }) => (
  <img src="/logo.png" alt="AMS" width={size} height={size} style={{ objectFit: "contain", flexShrink: 0 }} />
);

// ─── SERVICE CATALOG ──────────────────────────────────────────────────────────
const SERVICE_CATALOG = {
  duomind: {
    id: "duomind", name: "DuoMind: Finn & Zinn", track: "foundation",
    price: "$297/mo", color: "#60a5fa",
    desc: "Finn handles missed calls & follow-up. Zinn is the owner's personal Telegram AI assistant.",
    steps: ["Finn Integration Setup","Missed Call Text-Back Configured","Zinn Telegram Connection Active","DuoMind Go Live"],
  },
  core: {
    id: "core", name: "C.O.R.E. Revenue Guard", track: "foundation",
    price: "$1,297/mo", color: "#8b5cf6",
    desc: "STAN voice AI (receptionist mode), missed call text-back, review monitoring, DM auto-response.",
    steps: ["STAN Voice Agent Configured","Voice Approved","Phone Number Assigned","Missed Call Text-Back Active","Review Monitoring Live","DM Auto-Response Active","C.O.R.E. Go Live"],
  },
  velocity: {
    id: "velocity", name: "Revenue Velocity", track: "stack",
    price: "$1,697/mo", color: "#f59e0b", requires: "core",
    desc: "STAN in booking mode + reputation engine + pipeline visibility dashboard.",
    steps: ["STAN Upgraded to Booking Mode","Calendar Integration Connected","Reputation Engine Active","RevEdge Hub Dashboard Live","Revenue Velocity Go Live"],
  },
  autopilot: {
    id: "autopilot", name: "Revenue Autopilot", track: "stack",
    price: "$2,197/mo", color: "#10b981", requires: "velocity",
    desc: "Full automation — everything in C.O.R.E. + Velocity + voice AI web chatbot included.",
    steps: ["Autopilot Configuration Complete","Voice AI Web Chatbot Deployed","Revenue Autopilot Go Live"],
  },
  pulse: {
    id: "pulse", name: "Pulse / AEO/GEO", track: "addon",
    price: "$597/mo", color: "#3b82f6",
    desc: "AI visibility campaign — get found in ChatGPT, Gemini, and Perplexity searches.",
    steps: ["AI Visibility Audit Delivered","GEO Citations Submitted","AI Engine Indexing","First Visibility Report","Pulse Go Live"],
  },
  contentarmy: {
    id: "contentarmy", name: "ContentArmy", track: "addon",
    price: "$297/mo+", color: "#ec4899",
    desc: "AI content engine — SEO/GEO-optimized blog posts and social content on autopilot.",
    steps: ["Content Strategy Approved","First Content Batch Delivered","ContentArmy Active"],
  },
  apex: {
    id: "apex", name: "Apex Advantage Engine", track: "addon",
    price: "Custom", color: "#f97316",
    desc: "Intelligence suite — SCOUT competitor recon, VRS niche analysis, Watcher monitoring.",
    steps: ["SCOUT Report Delivered","VRS Niche Analysis Complete","Watcher Monitoring Active","Apex Intelligence Live"],
  },
};

const SERVICE_ORDER = ["duomind","core","velocity","autopilot","pulse","contentarmy","apex"];
const UNIVERSAL_STEPS = ["Contract Signed","Business Info Collected"];

const VERTICALS = [
  "Septic Services","HVAC","Plumbing","Auto Repair","Pest Control",
  "Electrical","Roofing","Landscaping","Pool & Spa","Cleaning Services",
  "Med Spa","Salon & Beauty","Dental","Chiropractic","Real Estate",
  "Legal","Financial Services","Other",
];
const TIMEZONES = ["HST","AKST","PST","MST","CST","EST","Other"];
const FOLLOW_UP_OPTIONS = ["Phone","SMS","Email"];
// ─── API LAYER ───────────────────────────────────────────────────────────────
// After deploying the Worker, update this URL to match your workers.dev subdomain.
// For local dev: run `npx wrangler dev` in the worker/ folder (serves on :8787)
const API_BASE = window.location.hostname === "localhost"
  ? "http://localhost:8787"
  : "https://ams-onboarding-api.biglexlost.workers.dev";

async function apiFetch(path, { token, method = "GET", body } = {}) {
  const headers = { "Content-Type": "application/json" };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  return fetch(`${API_BASE}${path}`, {
    method,
    headers,
    body: body != null ? JSON.stringify(body) : undefined,
  });
}

function generateSteps(selectedServices) {
  let id = 1;
  const steps = UNIVERSAL_STEPS.map(label => ({
    id: id++, label, service: "universal", status: "pending", date: "TBD"
  }));
  SERVICE_ORDER.forEach(svcId => {
    if (selectedServices.includes(svcId)) {
      SERVICE_CATALOG[svcId].steps.forEach(label => {
        steps.push({ id: id++, label, service: svcId, status: "pending", date: "TBD" });
      });
    }
  });
  return steps;
}

function computeRate(services) {
  let base = 0;
  if (services.includes("autopilot")) base = 2197;
  else if (services.includes("velocity")) base = 1697;
  else if (services.includes("core")) base = 1297;
  if (services.includes("duomind") && !services.includes("core")) base += 297;
  if (services.includes("pulse")) base += 597;
  if (services.includes("contentarmy")) base += 297;
  return base > 0 ? `$${base.toLocaleString()}/mo` : "TBD";
}

function getClientIdFromURL() {
  const match = window.location.pathname.match(/\/client\/([a-zA-Z0-9]+)/);
  if (match) return match[1];
  return new URLSearchParams(window.location.search).get("id") || null;
}
function getDaysUntil(d) {
  return Math.ceil((new Date(d) - new Date()) / 86400000);
}
function getProgress(steps) {
  return Math.round((steps.filter(s => s.status === "complete").length / steps.length) * 100);
}
function randomId() {
  return Math.random().toString(36).slice(2, 9);
}

// Mock data removed — all client data now lives in Cloudflare D1 via the Worker API.

// ─── STYLES ───────────────────────────────────────────────────────────────────
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=DM+Sans:wght@300;400;500;600&family=JetBrains+Mono:wght@400;600&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  :root {
    --bg: #0a0e1a; --bg2: #0f1528; --bg3: #151c35;
    --card: #111827; --card2: #1a2440;
    --border: #1e2d50; --border2: #253460;
    --accent: #3b82f6; --accent2: #60a5fa; --accent-glow: rgba(59,130,246,0.15);
    --success: #10b981; --success-bg: rgba(16,185,129,0.1);
    --warning: #f59e0b; --warning-bg: rgba(245,158,11,0.1);
    --purple: #8b5cf6; --purple-bg: rgba(139,92,246,0.1);
    --muted: #64748b; --text: #e2e8f0; --text2: #94a3b8; --text3: #64748b;
    --serif: 'DM Serif Display', Georgia, serif;
    --sans: 'DM Sans', system-ui, sans-serif;
    --mono: 'JetBrains Mono', monospace;
    --radius: 12px; --radius2: 8px;
  }
  html, body, #root { min-height: 100vh; background: var(--bg); color: var(--text); font-family: var(--sans); font-size: 15px; line-height: 1.6; }
  body { background-image: radial-gradient(ellipse 80% 50% at 50% -20%, rgba(59,130,246,0.08) 0%, transparent 60%), radial-gradient(ellipse 40% 30% at 80% 80%, rgba(16,185,129,0.04) 0%, transparent 50%); background-attachment: fixed; }
  .app { min-height: 100vh; display: flex; flex-direction: column; }

  /* AUTH */
  .auth-wrap { min-height: 100vh; display: flex; align-items: center; justify-content: center; padding: 24px; }
  .auth-card { width: 100%; max-width: 420px; background: var(--card); border: 1px solid var(--border); border-radius: 20px; padding: 48px 40px; position: relative; overflow: hidden; animation: fadeUp 0.5s ease both; }
  .auth-card::before { content: ''; position: absolute; top: 0; left: 0; right: 0; height: 2px; background: linear-gradient(90deg, transparent, var(--accent), transparent); }
  .auth-logo { font-family: var(--serif); font-size: 1.6rem; color: var(--text); margin-bottom: 4px; letter-spacing: -0.02em; }
  .auth-logo span { color: var(--accent2); }
  .auth-sub { color: var(--text3); font-size: 0.85rem; margin-bottom: 36px; font-weight: 300; text-align: center; }
  .auth-label { display: block; font-size: 0.75rem; font-weight: 600; letter-spacing: 0.08em; text-transform: uppercase; color: var(--text3); margin-bottom: 8px; }
  .auth-input { width: 100%; background: var(--bg2); border: 1px solid var(--border); border-radius: var(--radius2); padding: 14px 16px; color: var(--text); font-family: var(--mono); font-size: 1.1rem; letter-spacing: 0.2em; outline: none; transition: border-color 0.2s, box-shadow 0.2s; margin-bottom: 20px; }
  .auth-input:focus { border-color: var(--accent); box-shadow: 0 0 0 3px var(--accent-glow); }
  .auth-btn { width: 100%; background: var(--accent); color: white; border: none; border-radius: var(--radius2); padding: 14px; font-family: var(--sans); font-size: 0.9rem; font-weight: 600; cursor: pointer; transition: background 0.2s, transform 0.1s; letter-spacing: 0.02em; }
  .auth-btn:hover { background: #2563eb; }
  .auth-btn:active { transform: scale(0.98); }
  .auth-btn:disabled { opacity: 0.5; cursor: not-allowed; }
  .auth-error { color: #f87171; font-size: 0.8rem; margin-top: -12px; margin-bottom: 16px; padding: 10px 12px; background: rgba(248,113,113,0.08); border-radius: var(--radius2); border: 1px solid rgba(248,113,113,0.2); }

  /* HEADER */
  .header { border-bottom: 1px solid var(--border); background: rgba(10,14,26,0.8); backdrop-filter: blur(12px); position: sticky; top: 0; z-index: 100; padding: 0 32px; height: 64px; display: flex; align-items: center; justify-content: space-between; }
  .header-logo { font-family: var(--serif); font-size: 1.2rem; letter-spacing: -0.02em; display: flex; align-items: center; gap: 10px; }
  .header-logo span { color: var(--accent2); }
  .header-right { display: flex; align-items: center; gap: 12px; }
  .admin-badge { background: var(--warning-bg); border: 1px solid rgba(245,158,11,0.3); color: var(--warning); font-size: 0.7rem; font-weight: 600; letter-spacing: 0.1em; text-transform: uppercase; padding: 4px 10px; border-radius: 100px; }
  .header-btn { background: var(--bg3); border: 1px solid var(--border); color: var(--text2); border-radius: var(--radius2); padding: 6px 14px; font-size: 0.8rem; font-weight: 500; cursor: pointer; transition: all 0.2s; font-family: var(--sans); }
  .header-btn:hover { border-color: var(--accent); color: var(--accent2); }

  /* LAYOUT */
  .main { flex: 1; padding: 40px 32px; max-width: 1100px; margin: 0 auto; width: 100%; }

  /* HERO */
  .hero { margin-bottom: 32px; animation: fadeUp 0.4s ease both; }
  .hero-greeting { font-family: var(--serif); font-size: 2.2rem; letter-spacing: -0.03em; line-height: 1.2; margin-bottom: 10px; }
  .hero-greeting span { color: var(--accent2); font-style: italic; }
  .hero-meta { color: var(--text3); font-size: 0.85rem; display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
  .chip { background: var(--bg3); border: 1px solid var(--border); border-radius: 100px; padding: 3px 12px; font-size: 0.75rem; color: var(--text2); }
  .chip.accent { background: var(--accent-glow); border-color: rgba(59,130,246,0.3); color: var(--accent2); }

  /* SERVICE CHIPS on hero */
  .svc-chip { display: inline-flex; align-items: center; gap: 5px; padding: 3px 10px; border-radius: 100px; font-size: 0.7rem; font-weight: 600; letter-spacing: 0.05em; border: 1px solid; }

  /* GRID */
  .grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 20px; }
  .grid-3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 20px; margin-bottom: 20px; }
  @media (max-width: 768px) { .grid-2, .grid-3 { grid-template-columns: 1fr; } .main { padding: 24px 16px; } .header { padding: 0 16px; } }

  /* CARDS */
  .card { background: var(--card); border: 1px solid var(--border); border-radius: var(--radius); padding: 24px; animation: fadeUp 0.5s ease both; }
  .card-label { font-size: 0.7rem; font-weight: 600; letter-spacing: 0.1em; text-transform: uppercase; color: var(--text3); margin-bottom: 8px; }
  .card-value { font-family: var(--serif); font-size: 1.8rem; letter-spacing: -0.02em; line-height: 1.2; }
  .card-sub { font-size: 0.78rem; color: var(--text3); margin-top: 4px; }

  /* STATUS CARD */
  .status-card { background: linear-gradient(135deg, var(--bg2), var(--bg3)); border: 1px solid var(--border2); border-radius: var(--radius); padding: 24px 28px; margin-bottom: 20px; position: relative; overflow: hidden; animation: fadeUp 0.45s ease both; }
  .status-card::before { content: ''; position: absolute; left: 0; top: 0; bottom: 0; width: 3px; background: linear-gradient(180deg, var(--accent), var(--success)); border-radius: 0 2px 2px 0; }
  .status-card-label { font-size: 0.7rem; font-weight: 600; letter-spacing: 0.1em; text-transform: uppercase; color: var(--accent2); margin-bottom: 10px; }
  .status-card-text { color: var(--text2); font-size: 0.9rem; line-height: 1.7; font-weight: 300; }

  /* PROGRESS */
  .section-title { font-family: var(--serif); font-size: 1.15rem; letter-spacing: -0.01em; }
  .progress-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; }
  .progress-pct { font-family: var(--mono); font-size: 0.85rem; color: var(--accent2); font-weight: 600; }
  .progress-bar-wrap { background: var(--bg3); border-radius: 100px; height: 6px; margin-bottom: 28px; overflow: hidden; }
  .progress-bar-fill { height: 100%; border-radius: 100px; background: linear-gradient(90deg, var(--accent), var(--success)); transition: width 1s cubic-bezier(0.4,0,0.2,1); position: relative; }
  .progress-bar-fill::after { content: ''; position: absolute; right: 0; top: 50%; transform: translateY(-50%); width: 10px; height: 10px; background: white; border-radius: 50%; box-shadow: 0 0 8px var(--accent); }
  .steps-list { display: flex; flex-direction: column; gap: 0; }
  .step-item { display: flex; align-items: flex-start; gap: 16px; padding: 12px 0; border-bottom: 1px solid var(--border); }
  .step-item:last-child { border-bottom: none; }
  .step-icon { width: 28px; height: 28px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 0.7rem; flex-shrink: 0; margin-top: 3px; font-weight: 700; }
  .step-icon.complete { background: var(--success-bg); border: 1.5px solid var(--success); color: var(--success); }
  .step-icon.in_progress { background: var(--accent-glow); border: 1.5px solid var(--accent); color: var(--accent2); }
  .step-icon.pending { background: var(--bg3); border: 1.5px solid var(--border2); color: var(--text3); }
  .step-content { flex: 1; }
  .step-label { font-weight: 500; font-size: 0.88rem; margin-bottom: 3px; }
  .step-label.pending { color: var(--text3); }
  .step-date { font-size: 0.72rem; color: var(--text3); font-family: var(--mono); }
  .step-badge { font-size: 0.62rem; font-weight: 600; letter-spacing: 0.08em; text-transform: uppercase; padding: 2px 7px; border-radius: 100px; flex-shrink: 0; margin-top: 5px; }
  .step-badge.complete { background: var(--success-bg); color: var(--success); }
  .step-badge.in_progress { background: var(--accent-glow); color: var(--accent2); }
  .step-badge.pending { background: var(--bg3); color: var(--text3); }

  /* STEP SERVICE GROUP HEADER */
  .step-group-header { display: flex; align-items: center; gap: 8px; padding: 16px 0 8px; font-size: 0.68rem; font-weight: 700; letter-spacing: 0.12em; text-transform: uppercase; color: var(--text3); border-bottom: 1px solid var(--border); margin-bottom: 4px; }
  .step-group-dot { width: 6px; height: 6px; border-radius: 50%; flex-shrink: 0; }

  /* KEYWORDS */
  .keywords-wrap { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 12px; }
  .keyword-chip { background: var(--bg3); border: 1px solid var(--border2); border-radius: var(--radius2); padding: 5px 12px; font-size: 0.78rem; color: var(--text2); font-family: var(--mono); }

  /* VISIBILITY */
  .visibility-value { font-family: var(--mono); font-size: 0.9rem; color: var(--warning); background: var(--warning-bg); border: 1px solid rgba(245,158,11,0.2); border-radius: var(--radius2); padding: 10px 14px; margin-top: 12px; }

  /* PIN */
  .pin-section { background: var(--card); border: 1px solid var(--border); border-radius: var(--radius); padding: 24px; margin-bottom: 20px; }
  .pin-section.alert { border-color: rgba(245,158,11,0.3); background: linear-gradient(135deg, var(--card), rgba(245,158,11,0.03)); }
  .pin-row { display: flex; gap: 12px; align-items: flex-end; flex-wrap: wrap; margin-top: 16px; }
  .pin-input-wrap { flex: 1; min-width: 120px; }
  .pin-input-label { font-size: 0.7rem; font-weight: 600; letter-spacing: 0.08em; text-transform: uppercase; color: var(--text3); margin-bottom: 6px; display: block; }
  .pin-input { width: 100%; background: var(--bg2); border: 1px solid var(--border); border-radius: var(--radius2); padding: 10px 14px; color: var(--text); font-family: var(--mono); font-size: 1rem; letter-spacing: 0.2em; outline: none; }
  .pin-input:focus { border-color: var(--accent); }
  .pin-save-btn { background: var(--accent); color: white; border: none; border-radius: var(--radius2); padding: 10px 20px; font-family: var(--sans); font-size: 0.85rem; font-weight: 600; cursor: pointer; white-space: nowrap; }
  .pin-save-btn:hover { background: #2563eb; }
  .pin-msg { font-size: 0.8rem; margin-top: 10px; }
  .pin-msg.success { color: var(--success); }
  .pin-msg.error { color: #f87171; }

  /* ADMIN PANEL */
  .admin-panel { background: linear-gradient(135deg, var(--card), rgba(245,158,11,0.03)); border: 1px solid rgba(245,158,11,0.2); border-radius: var(--radius); padding: 28px; margin-bottom: 20px; }
  .admin-title { font-family: var(--serif); font-size: 1.1rem; color: var(--warning); margin-bottom: 20px; display: flex; align-items: center; gap: 8px; }
  .client-list { display: flex; flex-direction: column; gap: 10px; margin-bottom: 20px; }
  .client-row { background: var(--bg2); border: 1px solid var(--border); border-radius: var(--radius2); padding: 14px 18px; display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 10px; cursor: pointer; transition: border-color 0.2s; }
  .client-row:hover { border-color: var(--accent); }
  .client-row.active { border-color: var(--accent); background: var(--accent-glow); }
  .client-row-name { font-weight: 600; font-size: 0.9rem; }
  .client-row-meta { font-size: 0.72rem; color: var(--text3); font-family: var(--mono); margin-top: 2px; }
  .client-row-right { display: flex; align-items: center; gap: 8px; }
  .client-row-progress { font-family: var(--mono); font-size: 0.78rem; color: var(--accent2); }
  .copy-url-btn { background: var(--bg3); border: 1px solid var(--border); color: var(--text2); border-radius: var(--radius2); padding: 4px 10px; font-size: 0.72rem; cursor: pointer; font-family: var(--mono); transition: all 0.2s; }
  .copy-url-btn:hover { border-color: var(--accent); color: var(--accent2); }
  .new-client-btn { background: var(--accent-glow); border: 1px dashed var(--accent); color: var(--accent2); border-radius: var(--radius2); padding: 12px 18px; font-size: 0.85rem; font-weight: 600; cursor: pointer; width: 100%; text-align: center; margin-bottom: 20px; font-family: var(--sans); transition: all 0.2s; }
  .new-client-btn:hover { background: rgba(59,130,246,0.2); }

  /* ADMIN EDITOR */
  .admin-editor { margin-top: 4px; }
  .editor-section { margin-bottom: 28px; }
  .editor-section-title { font-size: 0.72rem; font-weight: 700; letter-spacing: 0.12em; text-transform: uppercase; color: var(--warning); margin-bottom: 14px; padding-bottom: 8px; border-bottom: 1px solid rgba(245,158,11,0.2); }
  .fields-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
  .fields-grid.cols-3 { grid-template-columns: 1fr 1fr 1fr; }
  .field-wrap { display: flex; flex-direction: column; gap: 5px; }
  .field-wrap.full { grid-column: 1 / -1; }
  .field-lbl { font-size: 0.68rem; font-weight: 600; letter-spacing: 0.08em; text-transform: uppercase; color: var(--text3); }
  .field-inp { background: var(--bg2); border: 1px solid var(--border); border-radius: var(--radius2); padding: 9px 12px; color: var(--text); font-family: var(--sans); font-size: 0.85rem; outline: none; transition: border-color 0.2s; width: 100%; }
  .field-inp:focus { border-color: var(--accent); }
  .field-sel { background: var(--bg2); border: 1px solid var(--border); border-radius: var(--radius2); padding: 9px 12px; color: var(--text); font-family: var(--sans); font-size: 0.85rem; outline: none; width: 100%; cursor: pointer; }

  /* SERVICE SELECTOR */
  .track-section { margin-bottom: 20px; }
  .track-label { font-size: 0.68rem; font-weight: 700; letter-spacing: 0.12em; text-transform: uppercase; color: var(--text3); margin-bottom: 4px; }
  .track-sublabel { font-size: 0.75rem; color: var(--text3); margin-bottom: 10px; }
  .service-cards { display: flex; flex-direction: column; gap: 8px; }
  .service-card { background: var(--bg2); border: 1px solid var(--border); border-radius: var(--radius2); padding: 12px 16px; cursor: pointer; transition: all 0.2s; display: flex; align-items: flex-start; gap: 12px; }
  .service-card:hover { border-color: var(--border2); }
  .service-card.selected { border-color: var(--accent); background: var(--accent-glow); }
  .service-card.disabled { opacity: 0.4; cursor: not-allowed; }
  .service-card-check { width: 18px; height: 18px; border-radius: 4px; border: 2px solid var(--border2); flex-shrink: 0; margin-top: 2px; display: flex; align-items: center; justify-content: center; transition: all 0.2s; }
  .service-card.selected .service-card-check { background: var(--accent); border-color: var(--accent); color: white; font-size: 11px; }
  .service-card-body { flex: 1; }
  .service-card-top { display: flex; align-items: center; gap: 8px; margin-bottom: 2px; }
  .service-card-name { font-weight: 600; font-size: 0.88rem; }
  .service-card-price { font-family: var(--mono); font-size: 0.72rem; color: var(--accent2); margin-left: auto; }
  .service-card-desc { font-size: 0.78rem; color: var(--text3); line-height: 1.5; }

  /* STACK FLOW */
  .stack-flow { display: flex; align-items: stretch; gap: 8px; }
  .stack-item { flex: 1; background: var(--bg2); border: 1px solid var(--border); border-radius: var(--radius2); padding: 12px; cursor: pointer; transition: all 0.2s; position: relative; }
  .stack-item:hover:not(.stack-disabled) { border-color: var(--border2); }
  .stack-item.stack-selected { border-color: var(--accent); background: var(--accent-glow); }
  .stack-item.stack-disabled { opacity: 0.35; cursor: not-allowed; }
  .stack-item-name { font-weight: 600; font-size: 0.82rem; margin-bottom: 2px; }
  .stack-item-price { font-family: var(--mono); font-size: 0.68rem; color: var(--accent2); margin-bottom: 4px; }
  .stack-item-desc { font-size: 0.72rem; color: var(--text3); line-height: 1.4; }
  .stack-check { position: absolute; top: 8px; right: 8px; width: 16px; height: 16px; border-radius: 50%; background: var(--accent); color: white; display: flex; align-items: center; justify-content: center; font-size: 9px; font-weight: 700; }
  .stack-arrow { display: flex; align-items: center; color: var(--text3); font-size: 0.8rem; flex-shrink: 0; }

  /* ADMIN STEPS EDITOR */
  .steps-editor { display: flex; flex-direction: column; gap: 6px; }
  .step-editor-group { margin-bottom: 12px; }
  .step-editor-group-label { font-size: 0.65rem; font-weight: 700; letter-spacing: 0.12em; text-transform: uppercase; color: var(--text3); margin-bottom: 6px; }
  .step-edit-row { display: flex; align-items: center; gap: 8px; background: var(--bg2); border: 1px solid var(--border); border-radius: var(--radius2); padding: 8px 12px; }
  .step-edit-label { flex: 1; font-size: 0.82rem; color: var(--text2); }
  .step-edit-date { background: var(--bg3); border: 1px solid var(--border); border-radius: 6px; padding: 3px 8px; color: var(--text); font-size: 0.72rem; font-family: var(--mono); outline: none; width: 110px; }
  .step-sel { background: var(--bg3); border: 1px solid var(--border); border-radius: 6px; color: var(--text); font-size: 0.72rem; padding: 3px 6px; outline: none; cursor: pointer; font-family: var(--sans); }

  /* ADMIN TEXTAREA */
  .admin-textarea { width: 100%; background: var(--bg2); border: 1px solid var(--border); border-radius: var(--radius2); padding: 12px 16px; color: var(--text); font-family: var(--sans); font-size: 0.88rem; outline: none; resize: vertical; min-height: 90px; transition: border-color 0.2s; line-height: 1.6; }
  .admin-textarea:focus { border-color: var(--accent); }
  .admin-save-btn { background: var(--warning); color: #0a0e1a; border: none; border-radius: var(--radius2); padding: 10px 28px; font-family: var(--sans); font-size: 0.88rem; font-weight: 700; cursor: pointer; transition: opacity 0.2s; }
  .admin-save-btn:hover { opacity: 0.85; }
  .admin-save-row { display: flex; align-items: center; gap: 12px; }
  .save-confirm { color: var(--success); font-size: 0.82rem; }
  .regen-btn { background: var(--bg3); border: 1px solid var(--border); color: var(--text2); border-radius: var(--radius2); padding: 8px 16px; font-size: 0.8rem; cursor: pointer; font-family: var(--sans); transition: all 0.2s; }
  .regen-btn:hover { border-color: var(--accent); color: var(--accent2); }

  /* MISC */
  .divider { height: 1px; background: var(--border); margin: 20px 0; }
  .footer { text-align: center; padding: 24px; color: var(--text3); font-size: 0.75rem; border-top: 1px solid var(--border); margin-top: 40px; }
  .footer span { color: var(--accent2); }
  @keyframes fadeUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
`;

// ─── STEP ICON ─────────────────────────────────────────────────────────────────
function StepIcon({ status }) {
  if (status === "complete") return <span>✓</span>;
  if (status === "in_progress") return <span>◉</span>;
  return <span>○</span>;
}

// ─── SERVICE SELECTOR ─────────────────────────────────────────────────────────
function ServiceSelector({ selected, onChange }) {
  const toggle = (id) => {
    const svc = SERVICE_CATALOG[id];
    if (svc.requires && !selected.includes(svc.requires)) return;
    if (selected.includes(id)) {
      // Remove this and anything that requires it
      const toRemove = SERVICE_ORDER.filter(s =>
        s === id || (SERVICE_CATALOG[s].requires && selected.includes(s) &&
          (SERVICE_CATALOG[s].requires === id || selected.indexOf(SERVICE_CATALOG[s].requires) >= selected.indexOf(id)))
      );
      onChange(selected.filter(s => !toRemove.includes(s)));
    } else {
      onChange([...selected, id]);
    }
  };

  const stackItems = ["core","velocity","autopilot"];
  const foundation = ["duomind"];
  const addons = ["pulse","contentarmy","apex"];

  return (
    <div>
      {/* Foundation */}
      <div className="track-section">
        <div className="track-label">Foundation — Start Here</div>
        <div className="track-sublabel">Capture all inbound opportunities before driving more traffic in.</div>
        <div className="service-cards">
          {foundation.map(id => {
            const svc = SERVICE_CATALOG[id];
            const isSel = selected.includes(id);
            return (
              <div key={id} className={`service-card ${isSel ? "selected" : ""}`} onClick={() => toggle(id)}>
                <div className="service-card-check">{isSel && "✓"}</div>
                <div className="service-card-body">
                  <div className="service-card-top">
                    <span className="service-card-name">{svc.name}</span>
                    <span className="service-card-price">{svc.price}</span>
                  </div>
                  <div className="service-card-desc">{svc.desc}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Revenue Stack */}
      <div className="track-section">
        <div className="track-label">Revenue Stack — Sequential Upgrades</div>
        <div className="track-sublabel">Select the client's current tier. Each level includes everything below it.</div>
        <div className="stack-flow">
          {stackItems.map((id, i) => {
            const svc = SERVICE_CATALOG[id];
            const isSel = selected.includes(id);
            const isDisabled = svc.requires && !selected.includes(svc.requires);
            return (
              <>
                {i > 0 && <div className="stack-arrow">→</div>}
                <div
                  key={id}
                  className={`stack-item ${isSel ? "stack-selected" : ""} ${isDisabled ? "stack-disabled" : ""}`}
                  onClick={() => !isDisabled && toggle(id)}
                >
                  {isSel && <div className="stack-check">✓</div>}
                  <div className="stack-item-name">{svc.name}</div>
                  <div className="stack-item-price">{svc.price}</div>
                  <div className="stack-item-desc">{svc.desc}</div>
                </div>
              </>
            );
          })}
        </div>
      </div>

      {/* Add-ons */}
      <div className="track-section">
        <div className="track-label">Add-Ons — Available Anytime</div>
        <div className="track-sublabel">Can be added at any time. Apex preferred after foundation is established.</div>
        <div className="service-cards">
          {addons.map(id => {
            const svc = SERVICE_CATALOG[id];
            const isSel = selected.includes(id);
            return (
              <div key={id} className={`service-card ${isSel ? "selected" : ""}`} onClick={() => toggle(id)}>
                <div className="service-card-check">{isSel && "✓"}</div>
                <div className="service-card-body">
                  <div className="service-card-top">
                    <span className="service-card-name">{svc.name}</span>
                    <span className="service-card-price">{svc.price}</span>
                  </div>
                  <div className="service-card-desc">{svc.desc}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── STEPS EDITOR ─────────────────────────────────────────────────────────────
function StepsEditor({ steps, services, onChange }) {
  function updateStep(id, field, val) {
    onChange(steps.map(s => s.id === id ? { ...s, [field]: val } : s));
  }

  const grouped = {};
  steps.forEach(s => {
    const key = s.service;
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(s);
  });

  const groupOrder = ["universal", ...SERVICE_ORDER.filter(id => services.includes(id))];

  function groupLabel(key) {
    if (key === "universal") return "Universal";
    return SERVICE_CATALOG[key]?.name || key;
  }
  function groupColor(key) {
    if (key === "universal") return "var(--text3)";
    return SERVICE_CATALOG[key]?.color || "var(--text3)";
  }

  return (
    <div className="steps-editor">
      {groupOrder.map(key => {
        const groupSteps = grouped[key] || [];
        if (!groupSteps.length) return null;
        return (
          <div key={key} className="step-editor-group">
            <div className="step-editor-group-label" style={{ color: groupColor(key) }}>
              {groupLabel(key)}
            </div>
            {groupSteps.map(step => (
              <div key={step.id} className="step-edit-row">
                <div className="step-edit-label">{step.label}</div>
                <input
                  className="step-edit-date"
                  value={step.date}
                  onChange={e => updateStep(step.id, "date", e.target.value)}
                  placeholder="Est. date"
                />
                <select
                  className="step-sel"
                  value={step.status}
                  onChange={e => updateStep(step.id, "status", e.target.value)}
                >
                  <option value="pending">Pending</option>
                  <option value="in_progress">In Progress</option>
                  <option value="complete">Complete</option>
                </select>
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}

// ─── SERVICE CONFIG EDITOR ───────────────────────────────────────────────────
function ServiceConfigEditor({ services, config, onChange }) {
  function sc(field) {
    return (e) => onChange({ ...config, [field]: e.target.value });
  }
  function scCheck(field, val) {
    const cur = config[field] || [];
    const next = cur.includes(val) ? cur.filter(x => x !== val) : [...cur, val];
    onChange({ ...config, [field]: next });
  }

  if (!services.length) return null;

  return (
    <div>
      {/* ── DuoMind ─────────────────────────────────────────────────── */}
      {services.includes("duomind") && (
        <div style={{marginBottom:"20px"}}>
          <div className="editor-section-title" style={{color: SERVICE_CATALOG.duomind.color}}>
            DuoMind Config — Finn &amp; Zinn
          </div>
          <div className="fields-grid">
            <div className="field-wrap">
              <label className="field-lbl">Finn — Missed Call Phone #</label>
              <input className="field-inp" value={config.finnPhoneNumber || ""} onChange={sc("finnPhoneNumber")} placeholder="253-555-0000" />
            </div>
            <div className="field-wrap">
              <label className="field-lbl">Follow-Up Delay (minutes)</label>
              <input className="field-inp" type="number" value={config.finnFollowUpDelay || ""} onChange={sc("finnFollowUpDelay")} placeholder="5" />
            </div>
            <div className="field-wrap full">
              <label className="field-lbl">Finn — Custom Missed Call Text Template</label>
              <input className="field-inp" value={config.finnTextTemplate || ""} onChange={sc("finnTextTemplate")} placeholder="Hey {name}, sorry we missed you! How can we help?" />
            </div>
            <div className="field-wrap">
              <label className="field-lbl">Zinn — Telegram Handle</label>
              <input className="field-inp" value={config.zinnTelegramHandle || ""} onChange={sc("zinnTelegramHandle")} placeholder="@ownername_zinn" />
            </div>
            <div className="field-wrap">
              <label className="field-lbl">Zinn — Activation Status</label>
              <select className="field-sel" value={config.zinnStatus || "pending"} onChange={sc("zinnStatus")}>
                <option value="pending">Pending Setup</option>
                <option value="configuring">Configuring</option>
                <option value="active">Active</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* ── C.O.R.E. / STAN ─────────────────────────────────────────── */}
      {services.includes("core") && (
        <div style={{marginBottom:"20px"}}>
          <div className="editor-section-title" style={{color: SERVICE_CATALOG.core.color}}>
            C.O.R.E. Config — STAN Voice Agent
          </div>
          <div className="fields-grid">
            <div className="field-wrap">
              <label className="field-lbl">STAN Assigned Phone #</label>
              <input className="field-inp" value={config.stanPhoneNumber || ""} onChange={sc("stanPhoneNumber")} placeholder="253-555-0000" />
            </div>
            <div className="field-wrap">
              <label className="field-lbl">VAPI Assistant ID</label>
              <input className="field-inp" value={config.vapiAssistantId || ""} onChange={sc("vapiAssistantId")} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" style={{fontFamily:"var(--mono)",fontSize:"0.78rem"}} />
            </div>
            <div className="field-wrap">
              <label className="field-lbl">STAN Voice (ElevenLabs)</label>
              <input className="field-inp" value={config.stanVoiceName || ""} onChange={sc("stanVoiceName")} placeholder="e.g. Brian" />
            </div>
            <div className="field-wrap">
              <label className="field-lbl">GHL Sub-Account</label>
              <input className="field-inp" value={config.ghlSubAccount || ""} onChange={sc("ghlSubAccount")} placeholder="Sub-account name or ID" />
            </div>
            <div className="field-wrap full">
              <label className="field-lbl">Missed Call Text Template</label>
              <input className="field-inp" value={config.coreTextTemplate || ""} onChange={sc("coreTextTemplate")} placeholder="Hey {name}, STAN here from {business}! Sorry we missed you..." />
            </div>
            <div className="field-wrap">
              <label className="field-lbl">Voice Approval Status</label>
              <select className="field-sel" value={config.voiceApprovalStatus || "pending"} onChange={sc("voiceApprovalStatus")}>
                <option value="pending">Pending Client Review</option>
                <option value="approved">Approved</option>
                <option value="revision">Revision Requested</option>
              </select>
            </div>
            <div className="field-wrap">
              <label className="field-lbl">Review Platform</label>
              <select className="field-sel" value={config.reviewPlatform || ""} onChange={sc("reviewPlatform")}>
                <option value="">Select...</option>
                <option value="google">Google</option>
                <option value="yelp">Yelp</option>
                <option value="both">Google + Yelp</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* ── Revenue Velocity ────────────────────────────────────────── */}
      {services.includes("velocity") && (
        <div style={{marginBottom:"20px"}}>
          <div className="editor-section-title" style={{color: SERVICE_CATALOG.velocity.color}}>
            Revenue Velocity Config — Booking &amp; Reputation
          </div>
          <div className="fields-grid">
            <div className="field-wrap">
              <label className="field-lbl">Calendar Platform</label>
              <select className="field-sel" value={config.calendarPlatform || ""} onChange={sc("calendarPlatform")}>
                <option value="">Select...</option>
                <option value="ghl">Go High Level</option>
                <option value="calendly">Calendly</option>
                <option value="google">Google Calendar</option>
                <option value="acuity">Acuity</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div className="field-wrap">
              <label className="field-lbl">Booking Link</label>
              <input className="field-inp" value={config.bookingLink || ""} onChange={sc("bookingLink")} placeholder="https://calendly.com/..." />
            </div>
            <div className="field-wrap">
              <label className="field-lbl">RevEdge Hub Status</label>
              <select className="field-sel" value={config.revedgeStatus || "pending"} onChange={sc("revedgeStatus")}>
                <option value="pending">Pending Setup</option>
                <option value="configuring">Configuring</option>
                <option value="active">Active</option>
              </select>
            </div>
            <div className="field-wrap">
              <label className="field-lbl">STAN Booking Mode</label>
              <select className="field-sel" value={config.stanBookingMode || "pending"} onChange={sc("stanBookingMode")}>
                <option value="pending">Not Yet Upgraded</option>
                <option value="active">Booking Mode Active</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* ── Revenue Autopilot ───────────────────────────────────────── */}
      {services.includes("autopilot") && (
        <div style={{marginBottom:"20px"}}>
          <div className="editor-section-title" style={{color: SERVICE_CATALOG.autopilot.color}}>
            Revenue Autopilot Config — Web Chatbot
          </div>
          <div className="fields-grid">
            <div className="field-wrap">
              <label className="field-lbl">Web Chatbot Status</label>
              <select className="field-sel" value={config.chatbotStatus || "pending"} onChange={sc("chatbotStatus")}>
                <option value="pending">Pending Deployment</option>
                <option value="staging">In Staging</option>
                <option value="live">Live on Site</option>
              </select>
            </div>
            <div className="field-wrap">
              <label className="field-lbl">Chatbot Embed Domain</label>
              <input className="field-inp" value={config.chatbotDomain || ""} onChange={sc("chatbotDomain")} placeholder="economyseptic.com" />
            </div>
          </div>
        </div>
      )}

      {/* ── ContentArmy ─────────────────────────────────────────────── */}
      {services.includes("contentarmy") && (
        <div style={{marginBottom:"20px"}}>
          <div className="editor-section-title" style={{color: SERVICE_CATALOG.contentarmy.color}}>
            ContentArmy Config
          </div>
          <div className="fields-grid">
            <div className="field-wrap">
              <label className="field-lbl">Post Frequency</label>
              <select className="field-sel" value={config.contentFrequency || ""} onChange={sc("contentFrequency")}>
                <option value="">Select...</option>
                <option value="weekly">Weekly</option>
                <option value="biweekly">Bi-Weekly</option>
                <option value="monthly">Monthly</option>
                <option value="custom">Custom</option>
              </select>
            </div>
            <div className="field-wrap">
              <label className="field-lbl">Content Strategy Approved</label>
              <select className="field-sel" value={config.contentStrategyApproved || "no"} onChange={sc("contentStrategyApproved")}>
                <option value="no">Not Yet</option>
                <option value="yes">Approved</option>
              </select>
            </div>
            <div className="field-wrap full">
              <label className="field-lbl">Content Topics / Focus Areas (comma-separated)</label>
              <input className="field-inp" value={config.contentTopics || ""} onChange={sc("contentTopics")} placeholder="septic maintenance, drain care, fall prep..." />
            </div>
            <div className="field-wrap full">
              <label className="field-lbl">Target Platforms</label>
              <div style={{display:"flex",gap:"16px",flexWrap:"wrap",marginTop:"4px"}}>
                {["Blog","Google Business","Facebook","Instagram"].map(p => (
                  <label key={p} style={{display:"flex",alignItems:"center",gap:"6px",fontSize:"0.82rem",color:"var(--text2)",cursor:"pointer"}}>
                    <input type="checkbox" checked={(config.contentPlatforms || []).includes(p)} onChange={() => scCheck("contentPlatforms", p)} />
                    {p}
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Apex Advantage Engine ────────────────────────────────────── */}
      {services.includes("apex") && (
        <div style={{marginBottom:"20px"}}>
          <div className="editor-section-title" style={{color: SERVICE_CATALOG.apex.color}}>
            Apex Advantage Engine Config
          </div>
          <div className="fields-grid">
            <div className="field-wrap full">
              <label className="field-lbl">Competitor Domains to Monitor (comma-separated)</label>
              <input className="field-inp" value={config.competitorDomains || ""} onChange={sc("competitorDomains")} placeholder="competitor1.com, competitor2.com..." />
            </div>
            <div className="field-wrap">
              <label className="field-lbl">SCOUT Report Status</label>
              <select className="field-sel" value={config.scoutStatus || "pending"} onChange={sc("scoutStatus")}>
                <option value="pending">Not Started</option>
                <option value="in_progress">In Progress</option>
                <option value="delivered">Delivered</option>
              </select>
            </div>
            <div className="field-wrap">
              <label className="field-lbl">Report Frequency</label>
              <select className="field-sel" value={config.apexReportFrequency || ""} onChange={sc("apexReportFrequency")}>
                <option value="">Select...</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
              </select>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── UNIVERSAL FIELDS FORM ────────────────────────────────────────────────────
function UniversalFields({ client, onChange }) {
  const f = (field) => (e) => onChange({ ...client, [field]: e.target.value });
  return (
    <div className="fields-grid">
      <div className="field-wrap">
        <label className="field-lbl">Business Name</label>
        <input className="field-inp" value={client.businessName || ""} onChange={f("businessName")} placeholder="Economy Septic" />
      </div>
      <div className="field-wrap">
        <label className="field-lbl">Owner Name</label>
        <input className="field-inp" value={client.ownerName || ""} onChange={f("ownerName")} placeholder="John" />
      </div>
      <div className="field-wrap">
        <label className="field-lbl">Phone</label>
        <input className="field-inp" value={client.phone || ""} onChange={f("phone")} placeholder="253-555-1234" />
      </div>
      <div className="field-wrap">
        <label className="field-lbl">Email</label>
        <input className="field-inp" value={client.email || ""} onChange={f("email")} placeholder="owner@business.com" />
      </div>
      <div className="field-wrap">
        <label className="field-lbl">Website</label>
        <input className="field-inp" value={client.website || ""} onChange={f("website")} placeholder="businessname.com" />
      </div>
      <div className="field-wrap">
        <label className="field-lbl">Vertical / Industry</label>
        <select className="field-sel" value={client.vertical || ""} onChange={f("vertical")}>
          <option value="">Select vertical...</option>
          {VERTICALS.map(v => <option key={v} value={v}>{v}</option>)}
        </select>
      </div>
      <div className="field-wrap full">
        <label className="field-lbl">Service Area</label>
        <input className="field-inp" value={client.serviceArea || ""} onChange={f("serviceArea")} placeholder="Pierce County, WA" />
      </div>
      <div className="field-wrap">
        <label className="field-lbl">Business Hours</label>
        <input className="field-inp" value={client.businessHours || ""} onChange={f("businessHours")} placeholder="Mon-Fri 7am-5pm" />
      </div>
      <div className="field-wrap">
        <label className="field-lbl">Timezone</label>
        <select className="field-sel" value={client.timezone || ""} onChange={f("timezone")}>
          <option value="">Select...</option>
          {TIMEZONES.map(tz => <option key={tz} value={tz}>{tz}</option>)}
        </select>
      </div>
      <div className="field-wrap">
        <label className="field-lbl">Emergency Contact</label>
        <input className="field-inp" value={client.emergencyContact || ""} onChange={f("emergencyContact")} placeholder="253-555-9999" />
      </div>
      <div className="field-wrap">
        <label className="field-lbl">Preferred Follow-Up</label>
        <select className="field-sel" value={client.preferredFollowUp || ""} onChange={f("preferredFollowUp")}>
          <option value="">Select...</option>
          {FOLLOW_UP_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      </div>
      <div className="field-wrap">
        <label className="field-lbl">Start Date</label>
        <input className="field-inp" type="date" value={client.startDate || ""} onChange={f("startDate")} />
      </div>
      <div className="field-wrap">
        <label className="field-lbl">Est. Go-Live Date</label>
        <input className="field-inp" type="date" value={client.goLiveDate || ""} onChange={f("goLiveDate")} />
      </div>
    </div>
  );
}

// ─── ADMIN PANEL ──────────────────────────────────────────────────────────────
function AdminPanel({ token, onViewClient, onClientUpdated }) {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState(null);

  useEffect(() => {
    apiFetch("/api/admin/clients", { token })
      .then(r => r.json())
      .then(data => {
        const list = Array.isArray(data) ? data : [];
        setClients(list);
        if (list.length > 0) {
          setSelectedId(list[0].id);
          setDraft({ ...list[0] });
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token]);

  function selectClient(id) {
    const c = clients.find(x => x.id === id);
    if (!c) return;
    setSelectedId(id);
    setDraft({ ...c });
    setSaved(false); setSaveError(null);
  }

  async function addNewClient() {
    const id = randomId();
    const newClient = {
      id, pin: "0000", mustChangePIN: true,
      businessName: "", ownerName: "", phone: "", email: "", website: "",
      vertical: "", serviceArea: "", businessHours: "", timezone: "",
      emergencyContact: "", preferredFollowUp: "",
      services: [], monthlyRate: "TBD", startDate: "", goLiveDate: "",
      statusMessage: "Your onboarding is just getting started. We'll be in touch shortly with next steps.",
      currentAIVisibility: "Not yet measured", targetKeywords: [],
      onboardingSteps: generateSteps([]), notes: [],
    };
    const res = await apiFetch("/api/admin/clients", { token, method: "POST", body: newClient });
    if (res.ok) {
      const updated = [...clients, newClient];
      setClients(updated);
      setSelectedId(id);
      setDraft({ ...newClient });
      setSaved(false); setSaveError(null);
    }
  }

  function handleServicesChange(newServices) {
    setDraft(d => ({ ...d, services: newServices, monthlyRate: computeRate(newServices) }));
  }

  function regenSteps() {
    setDraft(d => ({ ...d, onboardingSteps: generateSteps(d.services) }));
  }

  async function handleSave() {
    setSaving(true); setSaveError(null);
    try {
      const res = await apiFetch(`/api/admin/clients/${draft.id}`, { token, method: "PUT", body: draft });
      if (res.ok) {
        const updatedClient = await res.json();
        setClients(prev => prev.map(c => c.id === updatedClient.id ? updatedClient : c));
        if (onClientUpdated) onClientUpdated(updatedClient);
        setSaved(true);
        setTimeout(() => setSaved(false), 2500);
      } else {
        const err = await res.json().catch(() => ({}));
        setSaveError(err.error || "Save failed");
      }
    } catch {
      setSaveError("Network error — check connection");
    } finally {
      setSaving(false);
    }
  }

  function copyURL(id) {
    navigator.clipboard.writeText(`${window.location.origin}?id=${id}`);
  }

  return (
    <div className="admin-panel">
      <div className="admin-title">⚡ Admin Control Panel</div>

      <button className="new-client-btn" onClick={addNewClient}>+ Add New Client</button>

      <div className="card-label">All Clients</div>
      {loading ? (
        <div style={{color:"var(--text3)",fontSize:"0.85rem",padding:"16px 0"}}>Loading clients...</div>
      ) : (
        <div className="client-list">
          {clients.map(c => (
            <div key={c.id} className={`client-row ${selectedId === c.id ? "active" : ""}`} onClick={() => selectClient(c.id)}>
              <div>
                <div className="client-row-name">{c.businessName || <span style={{color:"var(--text3)"}}>New Client</span>}</div>
                <div className="client-row-meta">{c.vertical || "—"} · {c.monthlyRate} · ID: {c.id}</div>
              </div>
              <div className="client-row-right">
                <div className="client-row-progress">{getProgress(c.onboardingSteps)}%</div>
                <button className="copy-url-btn" onClick={e => { e.stopPropagation(); copyURL(c.id); }}>Copy URL</button>
                <button className="copy-url-btn" onClick={e => { e.stopPropagation(); onViewClient(c.id); }}>View</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {draft && (
        <div className="admin-editor">
          <div className="divider" />
          <div className="card-label" style={{marginBottom:"16px"}}>
            Editing: {draft.businessName || "New Client"} <span style={{color:"var(--text3)",fontFamily:"var(--mono)",fontSize:"0.72rem"}}>({draft.id})</span>
          </div>

          {/* Universal Fields */}
          <div className="editor-section">
            <div className="editor-section-title">Universal Info</div>
            <UniversalFields client={draft} onChange={setDraft} />
          </div>

          {/* Service Selector */}
          <div className="editor-section">
            <div className="editor-section-title">Services</div>
            <ServiceSelector selected={draft.services} onChange={handleServicesChange} />
          </div>

          {/* Service-Specific Config */}
          {draft.services.length > 0 && (
            <div className="editor-section">
              <div className="editor-section-title">Service Configuration</div>
              <ServiceConfigEditor
                services={draft.services}
                config={draft.serviceConfig || {}}
                onChange={cfg => setDraft(d => ({ ...d, serviceConfig: cfg }))}
              />
            </div>
          )}

          {/* Status Message */}
          <div className="editor-section">
            <div className="editor-section-title">Status Message (Visible to Client)</div>
            <textarea
              className="admin-textarea"
              value={draft.statusMessage || ""}
              onChange={e => setDraft(d => ({ ...d, statusMessage: e.target.value }))}
            />
          </div>

          {/* AI Visibility — shown when Pulse is selected */}
          {draft.services.includes("pulse") && (
            <div className="editor-section">
              <div className="editor-section-title">Pulse / AI Visibility</div>
              <div className="fields-grid">
                <div className="field-wrap full">
                  <label className="field-lbl">Current AI Visibility</label>
                  <input className="field-inp" value={draft.currentAIVisibility || ""} onChange={e => setDraft(d => ({...d, currentAIVisibility: e.target.value}))} />
                </div>
                <div className="field-wrap full">
                  <label className="field-lbl">Target Keywords (comma-separated)</label>
                  <input className="field-inp" value={(draft.targetKeywords||[]).join(", ")} onChange={e => setDraft(d => ({...d, targetKeywords: e.target.value.split(",").map(k => k.trim()).filter(Boolean)}))} />
                </div>
              </div>
            </div>
          )}

          {/* Onboarding Steps */}
          <div className="editor-section">
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:"12px"}}>
              <div className="editor-section-title" style={{marginBottom:0}}>Onboarding Steps</div>
              <button className="regen-btn" onClick={regenSteps}>↺ Regenerate from Services</button>
            </div>
            <StepsEditor steps={draft.onboardingSteps} services={draft.services} onChange={steps => setDraft(d => ({...d, onboardingSteps: steps}))} />
          </div>

          <div className="admin-save-row">
            <button className="admin-save-btn" onClick={handleSave} disabled={saving}>
              {saving ? "Saving..." : "Save Client"}
            </button>
            {saved && <span className="save-confirm">✓ Saved</span>}
            {saveError && <span className="pin-msg error">{saveError}</span>}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── PIN CHANGE ───────────────────────────────────────────────────────────────
function PINChange({ token, clientId, currentPIN, onPINChanged, isFirstChange }) {
  const [newPIN, setNewPIN] = useState("");
  const [confirmPIN, setConfirmPIN] = useState("");
  const [msg, setMsg] = useState(null);
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    if (newPIN.length < 4) { setMsg({ type:"error", text:"PIN must be at least 4 digits." }); return; }
    if (newPIN !== confirmPIN) { setMsg({ type:"error", text:"PINs do not match." }); return; }
    setSaving(true);
    try {
      const res = await apiFetch(`/api/client/${clientId}/pin`, {
        token, method: "PUT",
        body: { currentPin: currentPIN, newPin: newPIN },
      });
      if (res.ok) {
        setMsg({ type:"success", text:"PIN updated!" });
        setNewPIN(""); setConfirmPIN("");
        setTimeout(onPINChanged, 1000);
      } else {
        const err = await res.json().catch(() => ({}));
        setMsg({ type:"error", text: err.error || "Failed to update PIN." });
      }
    } catch {
      setMsg({ type:"error", text:"Network error — try again." });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className={`pin-section ${isFirstChange ? "alert" : ""}`}>
      <div className="card-label" style={{color: isFirstChange ? "var(--warning)" : "var(--text3)"}}>
        {isFirstChange ? "⚠ Set Your PIN Before Continuing" : "Change Access PIN"}
      </div>
      {isFirstChange && <p style={{fontSize:"0.85rem",color:"var(--text2)",marginBottom:"4px"}}>You've been assigned a temporary PIN. Please set a personal one now.</p>}
      <div className="pin-row">
        <div className="pin-input-wrap">
          <label className="pin-input-label">New PIN</label>
          <input type="password" className="pin-input" maxLength={8} value={newPIN} onChange={e => setNewPIN(e.target.value.replace(/\D/g,""))} placeholder="••••" />
        </div>
        <div className="pin-input-wrap">
          <label className="pin-input-label">Confirm PIN</label>
          <input type="password" className="pin-input" maxLength={8} value={confirmPIN} onChange={e => setConfirmPIN(e.target.value.replace(/\D/g,""))} placeholder="••••" />
        </div>
        <button className="pin-save-btn" onClick={handleSave} disabled={saving}>{saving ? "Saving..." : "Save PIN"}</button>
      </div>
      {msg && <div className={`pin-msg ${msg.type}`}>{msg.text}</div>}
    </div>
  );
}

// ─── CLIENT DASHBOARD ─────────────────────────────────────────────────────────
function ClientDashboard({ client, isAdmin, token, onLogout, onAdminToggle, showAdmin, onViewClient, onClientUpdated }) {
  const [showPINChange, setShowPINChange] = useState(false);
  const progress = client ? getProgress(client.onboardingSteps) : 0;
  const daysLeft = client ? getDaysUntil(client.goLiveDate) : 0;

  // Group steps by service (guard against null client)
  const grouped = {};
  (client?.onboardingSteps || []).forEach(s => {
    if (!grouped[s.service]) grouped[s.service] = [];
    grouped[s.service].push(s);
  });
  const groupOrder = ["universal", ...SERVICE_ORDER.filter(id => (client?.services || []).includes(id))];

  function groupLabel(key) {
    if (key === "universal") return "Onboarding";
    return SERVICE_CATALOG[key]?.name || key;
  }
  function groupColor(key) {
    if (key === "universal") return "var(--text3)";
    return SERVICE_CATALOG[key]?.color || "var(--text3)";
  }

  return (
    <div className="app">
      <header className="header">
        <div className="header-logo">
          <Logo size={28} />
          Advantage <span>Marketing</span> Systems
        </div>
        <div className="header-right">
          {isAdmin && <span className="admin-badge">Admin</span>}
          {isAdmin && <button className="header-btn" onClick={onAdminToggle}>{showAdmin ? "Hide Admin" : "Admin Panel"}</button>}
          <button className="header-btn" onClick={() => setShowPINChange(v => !v)}>{showPINChange ? "Hide" : "Change PIN"}</button>
          <button className="header-btn" onClick={onLogout}>Sign Out</button>
        </div>
      </header>

      <main className="main">
        {showAdmin && isAdmin && (
          <AdminPanel token={token} onViewClient={onViewClient} onClientUpdated={onClientUpdated} />
        )}
        {showPINChange && client && (
          <PINChange token={token} clientId={client.id} onPINChanged={() => setShowPINChange(false)} isFirstChange={false} />
        )}

        {client ? (
          <>
            <div className="hero">
              <div className="hero-greeting">Welcome back, <span>{client.ownerName}</span>.</div>
              <div className="hero-meta">
                <span className="chip accent">{client.businessName}</span>
                <span className="chip">{client.vertical}</span>
                {client.services.map(svcId => (
                  <span key={svcId} className="svc-chip" style={{color: SERVICE_CATALOG[svcId]?.color, borderColor: SERVICE_CATALOG[svcId]?.color, background: SERVICE_CATALOG[svcId]?.color + "18"}}>
                    {SERVICE_CATALOG[svcId]?.name}
                  </span>
                ))}
                <span style={{color:"var(--text3)",fontSize:"0.8rem"}}>Since {client.startDate ? new Date(client.startDate).toLocaleDateString("en-US",{month:"long",day:"numeric",year:"numeric"}) : "—"}</span>
              </div>
            </div>

            <div className="status-card">
              <div className="status-card-label">Current Status Update</div>
              <div className="status-card-text">{client.statusMessage}</div>
            </div>

            <div className="grid-3">
              <div className="card">
                <div className="card-label">Overall Progress</div>
                <div className="card-value">{progress}%</div>
                <div className="card-sub">{client.onboardingSteps.filter(s => s.status === "complete").length} of {client.onboardingSteps.length} steps complete</div>
              </div>
              <div className="card">
                <div className="card-label">Est. Go-Live</div>
                <div className="card-value" style={{fontSize:"1.3rem"}}>
                  {client.goLiveDate ? new Date(client.goLiveDate).toLocaleDateString("en-US",{month:"short",day:"numeric"}) : "TBD"}
                </div>
                <div className="card-sub">{!client.goLiveDate ? "—" : daysLeft > 0 ? `${daysLeft} days away` : daysLeft === 0 ? "Today!" : "Live!"}</div>
              </div>
              <div className="card">
                <div className="card-label">Monthly Investment</div>
                <div className="card-value" style={{fontSize:"1.3rem"}}>{client.monthlyRate}</div>
                <div className="card-sub">{client.services.length} active service{client.services.length !== 1 ? "s" : ""}</div>
              </div>
            </div>

            <div className="card" style={{padding:"28px",marginBottom:"20px"}}>
              <div className="progress-header">
                <div className="section-title">Onboarding Milestones</div>
                <div className="progress-pct">{progress}% complete</div>
              </div>
              <div className="progress-bar-wrap">
                <div className="progress-bar-fill" style={{width:`${progress}%`}} />
              </div>
              <div className="steps-list">
                {groupOrder.map(key => {
                  const groupSteps = grouped[key] || [];
                  if (!groupSteps.length) return null;
                  return (
                    <div key={key}>
                      <div className="step-group-header">
                        <div className="step-group-dot" style={{background: groupColor(key)}} />
                        {groupLabel(key)}
                      </div>
                      {groupSteps.map(step => (
                        <div key={step.id} className="step-item">
                          <div className={`step-icon ${step.status}`}><StepIcon status={step.status} /></div>
                          <div className="step-content">
                            <div className={`step-label ${step.status === "pending" ? "pending" : ""}`}>{step.label}</div>
                            <div className="step-date">{step.date}</div>
                          </div>
                          <div className={`step-badge ${step.status}`}>
                            {step.status === "complete" ? "Done" : step.status === "in_progress" ? "In Progress" : "Pending"}
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            </div>

            {client.services.includes("pulse") && (
              <div className="grid-2">
                <div className="card">
                  <div className="card-label">Current AI Visibility</div>
                  <div style={{color:"var(--text2)",fontSize:"0.85rem",marginTop:"4px"}}>Where you appear when someone asks AI to recommend your service</div>
                  <div className="visibility-value">{client.currentAIVisibility}</div>
                </div>
                <div className="card">
                  <div className="card-label">Target Keywords</div>
                  <div style={{color:"var(--text2)",fontSize:"0.85rem",marginTop:"4px"}}>Queries we are optimizing your AI presence for</div>
                  <div className="keywords-wrap">
                    {(client.targetKeywords||[]).map((kw,i) => <div key={i} className="keyword-chip">{kw}</div>)}
                  </div>
                </div>
              </div>
            )}
          </>
        ) : isAdmin && !showAdmin ? (
          <div style={{textAlign:"center",padding:"80px 20px",color:"var(--text3)"}}>
            <div style={{fontSize:"2rem",marginBottom:"12px"}}>👆</div>
            <div style={{fontSize:"0.9rem"}}>Open the Admin Panel to select a client to view</div>
          </div>
        ) : null}
      </main>

      <footer className="footer">Powered by <span>Advantage Marketing Systems</span> · AMS Revenue Platform</footer>
    </div>
  );
}

// ─── AUTH SCREEN ──────────────────────────────────────────────────────────────
function AuthScreen({ clientId, onAuth }) {
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const isAdmin = !clientId;

  async function handleSubmit() {
    if (!pin) return;
    setLoading(true); setError("");
    try {
      const body = isAdmin ? { pin, role: "admin" } : { id: clientId, pin };
      const res = await apiFetch("/api/auth", { method: "POST", body });
      const data = await res.json();
      if (res.ok) {
        onAuth(data);
      } else {
        setError(data.error || "Authentication failed.");
      }
    } catch {
      setError("Network error — check your connection.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="auth-wrap">
      <div className="auth-card">
        <div className="auth-logo" style={{display:"flex",alignItems:"center",justifyContent:"center",gap:"10px"}}>
          <Logo size={32} />
          Advantage <span>Marketing</span> Systems
        </div>
        <div className="auth-sub">{isAdmin ? "Admin Access" : "Client Portal"}</div>
        <label className="auth-label">{isAdmin ? "Admin PIN" : "Your Access PIN"}</label>
        <input type="password" className="auth-input" maxLength={8} value={pin} onChange={e => setPin(e.target.value.replace(/\D/g,""))} onKeyDown={e => e.key === "Enter" && handleSubmit()} placeholder="••••" autoFocus />
        {error && <div className="auth-error">{error}</div>}
        <button className="auth-btn" onClick={handleSubmit} disabled={loading || !pin}>
          {loading ? "Verifying..." : "Access Portal"}
        </button>
        {!clientId && <div style={{textAlign:"center",marginTop:"20px",fontSize:"0.75rem",color:"var(--text3)"}}>Clients: use your unique link to access your portal</div>}
      </div>
    </div>
  );
}

// ─── ROOT ─────────────────────────────────────────────────────────────────────
export default function App() {
  const [token, setToken] = useState(null);
  const [role, setRole] = useState(null);          // "admin" | "client"
  const [currentClient, setCurrentClient] = useState(null);
  const [mustChangePIN, setMustChangePIN] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  const urlClientId = getClientIdFromURL();

  function handleAuth({ token: t, role: r, client }) {
    setToken(t);
    setRole(r);
    if (client) {
      setCurrentClient(client);
      setMustChangePIN(client.mustChangePIN || false);
    }
    if (r === "admin") setShowAdmin(true);
  }

  function handleLogout() {
    setToken(null); setRole(null); setCurrentClient(null);
    setShowAdmin(false); setMustChangePIN(false);
  }

  async function handleViewClient(id) {
    const res = await apiFetch(`/api/client/${id}`, { token });
    if (res.ok) {
      const client = await res.json();
      setCurrentClient(client);
      setShowAdmin(false);
    }
  }

  function handleClientUpdated(updatedClient) {
    if (currentClient && currentClient.id === updatedClient.id) {
      setCurrentClient(updatedClient);
    }
  }

  const isAdmin = role === "admin";
  const authed = !!token;

  return (
    <>
      <style>{styles}</style>
      {!authed ? (
        <AuthScreen clientId={urlClientId} onAuth={handleAuth} />
      ) : mustChangePIN && currentClient ? (
        <div className="app">
          <header className="header">
            <div className="header-logo">
              <Logo size={24} />
              Advantage <span>Marketing</span> Systems
            </div>
          </header>
          <main className="main">
            <div className="hero">
              <div className="hero-greeting">Welcome, <span>{currentClient.ownerName}</span>.</div>
              <div className="hero-meta"><span className="chip accent">{currentClient.businessName}</span></div>
            </div>
            <PINChange
              token={token}
              clientId={currentClient.id}
              onPINChanged={() => setMustChangePIN(false)}
              isFirstChange={true}
            />
          </main>
        </div>
      ) : (authed && (isAdmin || currentClient)) ? (
        <ClientDashboard
          client={currentClient}
          isAdmin={isAdmin}
          token={token}
          onLogout={handleLogout}
          onAdminToggle={() => setShowAdmin(v => !v)}
          showAdmin={showAdmin}
          onViewClient={handleViewClient}
          onClientUpdated={handleClientUpdated}
        />
      ) : null}
    </>
  );
}
